import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SimErrorBoundary } from "./sim-error-boundary";
import { motion } from "motion/react";
import { Expand, Pause, Play, RotateCcw, Shrink } from "lucide-react";
import type { SimDefinition, SimValues } from "@/lib/simulations/types";
import { cn } from "@/lib/utils";

function useThemeInk() {
  const probe = useRef<HTMLDivElement>(null);
  const [colors, setColors] = useState({
    ink: "#e6e6e6",
    muted: "#6b7280",
    accent: "#818cf8",
  });
  useEffect(() => {
    const el = probe.current;
    if (!el) return;
    const read = (cls: string) => {
      const span = el.querySelector<HTMLElement>(`[data-c="${cls}"]`);
      return span ? getComputedStyle(span).color : undefined;
    };
    setColors((c) => ({
      ink: read("ink") ?? c.ink,
      muted: read("muted") ?? c.muted,
      accent: read("accent") ?? c.accent,
    }));
  }, []);
  const node = (
    <div ref={probe} aria-hidden className="pointer-events-none absolute size-0 overflow-hidden">
      <span data-c="ink" className="text-foreground" />
      <span data-c="muted" className="text-muted-foreground" />
      <span data-c="accent" className="text-primary" />
    </div>
  );
  return { colors, node };
}

function SimCanvas({
  def,
  values,
  playing,
  resetKey,
}: {
  def: SimDefinition;
  values: SimValues;
  playing: boolean;
  resetKey: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { colors, node } = useThemeInk();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let raf = 0;
    let last = performance.now();
    let t = 0;
    // Cached box: reading getBoundingClientRect() inside the frame loop forces
    // a synchronous layout on every animation frame.
    let box = { width: 0, height: 0 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      box = { width: rect.width, height: rect.height };
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const frame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (playingRef.current) t += dt;
      ctx.clearRect(0, 0, box.width, box.height);
      try {
        def.draw({
          ctx,
          width: box.width,
          height: box.height,
          t,
          p: valuesRef.current,
          ...colors,
        });
      } catch {
        /* keep the loop alive on a bad parameter combination */
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    // Stop burning CPU/battery while the tab is in the background.
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      ro.disconnect();
    };
  }, [def, colors, resetKey]);

  return (
    <div className="relative">
      {node}
      <canvas ref={canvasRef} className="h-[260px] w-full rounded-[18px] lg:h-[420px] xl:h-[520px]" />
    </div>
  );
}

function SimGraph({ def, values }: { def: SimDefinition; values: SimValues }) {
  const pts = useMemo(() => {
    try {
      const raw = def.graph?.series?.(values);
      if (!Array.isArray(raw)) return [];
      return raw.filter(
        (p) => p && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)),
      );
    } catch {
      return [];
    }
  }, [def, values]);

  if (pts.length < 2) {
    return (
      <p className="py-6 text-center text-[11.5px] text-muted-foreground">
        No plottable data for these values — try adjusting a slider.
      </p>
    );
  }
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const W = 320;
  const H = 150;
  const pad = 18;
  const px = (x: number) => pad + ((x - minX) / (maxX - minX || 1)) * (W - pad * 2);
  const py = (y: number) => H - pad - ((y - minY) / (maxY - minY || 1)) * (H - pad * 2);
  const safe = (n: number) => (Number.isFinite(n) ? n : 0);
  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${safe(px(p.x)).toFixed(1)} ${safe(py(p.y)).toFixed(1)}`)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-[150px] w-full" role="img" aria-label="Simulation graph">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
      <line x1={pad} y1={pad} x2={pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
      <path d={d} fill="none" className="stroke-primary" strokeWidth={2} strokeLinecap="round" />
      <text x={W - pad} y={H - 5} textAnchor="end" className="fill-muted-foreground text-[9px]">
        {def.graph.xLabel}
      </text>
      <text x={4} y={12} className="fill-muted-foreground text-[9px]">
        {def.graph.yLabel}
      </text>
    </svg>
  );
}

const TABS = ["Theory", "Real life", "JEE", "Mistakes", "Practice", "Notes"] as const;
type Tab = (typeof TABS)[number];

export function SimPlayer(props: {
  def: SimDefinition;
  note: string;
  onNoteChange: (text: string) => void;
}) {
  return (
    <SimErrorBoundary resetKey={props.def?.concept}>
      <SimPlayerInner {...props} />
    </SimErrorBoundary>
  );
}

function SimPlayerInner({
  def,
  note,
  onNoteChange,
}: {
  def: SimDefinition;
  note: string;
  onNoteChange: (text: string) => void;
}) {
  const initial = useMemo(
    () =>
      Object.fromEntries(
        (def.params ?? []).map((p) => [p.key, Number.isFinite(Number(p.value)) ? Number(p.value) : 0]),
      ) as SimValues,
    [def],
  );
  const [values, setValues] = useState<SimValues>(initial);
  const [playing, setPlaying] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const [tab, setTab] = useState<Tab>("Theory");
  const [openAnswer, setOpenAnswer] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stageRef.current?.requestFullscreen?.().catch(() => setFullscreen(false));
  };

  useEffect(() => setValues(initial), [initial]);

  const observations = useMemo(() => {
    try {
      const out = def.observations?.(values);
      return Array.isArray(out) ? out.filter((o) => typeof o === "string") : [];
    } catch {
      return [];
    }
  }, [def, values]);

  /** Sanitises any control input: rejects NaN / null / Infinity and clamps to range. */
  const commitValue = useCallback(
    (key: string, raw: unknown, min: number, max: number, fallback: number) => {
      let n = Number(raw);
      if (!Number.isFinite(n)) n = fallback;
      const lo = Number.isFinite(min) ? min : -Infinity;
      const hi = Number.isFinite(max) ? max : Infinity;
      n = Math.min(Math.max(n, lo), hi);
      setValues((v) => (v[key] === n ? v : { ...v, [key]: n }));
    },
    [],
  );

  return (
    <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[300px_minmax(0,1fr)_360px] xl:items-start">
      {/* Workspace — the experiment itself dominates the screen */}
      <div className="order-1 flex min-w-0 flex-col gap-4 xl:order-2">
      <p className="text-[13px] leading-relaxed text-muted-foreground">{def.concept}</p>

      <div ref={stageRef} className="glass overflow-hidden rounded-[22px] bg-background p-2">

        <SimCanvas def={def} values={values} playing={playing} resetKey={resetKey} />
        <div className="flex items-center gap-2 px-2 pb-1 pt-2">
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            className="flex flex-1 items-center justify-center gap-2 rounded-full [background-image:var(--gradient-brand)] px-4 py-2 text-[12px] font-semibold text-primary-foreground transition-transform active:scale-95"
          >
            {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
            {playing ? "Pause" : "Play"}
          </button>
          <button
            type="button"
            onClick={() => {
              setValues(initial);
              setResetKey((k) => k + 1);
            }}
            className="glass flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition-transform active:scale-95"
          >
            <RotateCcw className="size-4" /> Reset
          </button>
          <button
            type="button"
            aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={toggleFullscreen}
            className="glass grid size-9 shrink-0 place-items-center rounded-full transition-transform active:scale-90"
          >
            {fullscreen ? <Shrink className="size-4" /> : <Expand className="size-4" />}
          </button>
        </div>
      </div>
      </div>

      {/* Left rail — instrument controls */}
      <div className="order-2 flex min-w-0 flex-col gap-4 xl:order-1">
      <div className="glass space-y-3 rounded-[20px] p-4">

        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Controls
        </h2>
        {(def.params ?? []).map((p) => (
          <label key={p.key} className="block">
            <span className="flex items-center justify-between text-[12px]">
              <span className="font-medium">{p.label}</span>
              <span className="font-semibold tabular-nums">
                {Number.isFinite(Number(values[p.key])) ? Number(values[p.key]).toFixed(2) : "—"}
                {p.unit ? ` ${p.unit}` : ""}
              </span>
            </span>
            <input
              type="range"
              min={p.min}
              max={p.max}
              step={p.step}
              value={Number.isFinite(Number(values[p.key])) ? Number(values[p.key]) : p.value}
              onChange={(e) => {
                // Read the value synchronously — the synthetic event is recycled
                // before a lazy state updater would run.
                const raw = e.target.value;
                commitValue(p.key, raw, p.min, p.max, p.value);
              }}
              className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-surface accent-primary"
            />
          </label>
        ))}
      </div>

      <div className="glass space-y-2 rounded-[20px] p-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Formulae
        </h2>
        {def.formula.map((f) => (
          <div key={f.expr} className="rounded-xl bg-surface/60 px-3 py-2">
            <p className="font-mono text-[13px] font-semibold">{f.expr}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{f.note}</p>
          </div>
        ))}
      </div>
      </div>

      {/* Right rail — results, observations and study material */}
      <div className="order-3 flex min-w-0 flex-col gap-4 xl:order-3">
      <div className="glass rounded-[20px] p-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Live graph
        </h2>

        <SimErrorBoundary inline resetKey={JSON.stringify(values)}>
          <SimGraph def={def} values={values} />
        </SimErrorBoundary>
        <ul className="mt-2 space-y-1.5">
          {observations.map((o) => (
            <li key={o} className="flex gap-2 text-[12px] text-muted-foreground">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
              {o}
            </li>
          ))}
        </ul>
        {def.conclusion ? (
          <p className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.07] p-3 text-[12px]">
            <span className="font-semibold text-emerald-300">Conclusion · </span>
            {def.conclusion}
          </p>
        ) : null}
      </div>

      <div className="glass rounded-[20px] p-4">
        <div className="flex gap-1 overflow-x-auto rounded-full bg-surface/60 p-1">
          {TABS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTab(item)}
              className={cn(
                "relative shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors",
                tab === item ? "text-primary-foreground" : "text-muted-foreground",
              )}
            >
              {tab === item ? (
                <motion.span
                  layoutId="sim-tab"
                  className="absolute inset-0 -z-10 rounded-full [background-image:var(--gradient-brand)]"
                  transition={{ type: "spring", stiffness: 300, damping: 28 }}
                />
              ) : null}
              {item}
            </button>
          ))}
        </div>

        <div className="mt-3">
          {tab === "Theory" ? (
            <ul className="space-y-2">
              {def.theory.map((line) => (
                <li key={line} className="flex gap-2 text-[12.5px] leading-relaxed">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                  {line}
                </li>
              ))}
            </ul>
          ) : null}

          {tab === "Real life" ? (
            <ul className="space-y-2">
              {(def.realLife ?? ["Look for this concept in everyday devices and lab setups."]).map(
                (line) => (
                  <li key={line} className="flex gap-2 text-[12.5px] leading-relaxed">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-emerald-400" />
                    {line}
                  </li>
                ),
              )}
            </ul>
          ) : null}

          {tab === "Practice" ? (
            <div className="space-y-2">
              {def.practice.map((item, i) => (
                <div key={item.q} className="rounded-xl bg-surface/60 p-3">
                  <p className="text-[12.5px] font-semibold">{item.q}</p>
                  <button
                    type="button"
                    onClick={() => setOpenAnswer(openAnswer === i ? null : i)}
                    className="mt-1 text-[11px] font-semibold text-primary"
                  >
                    {openAnswer === i ? "Hide solution" : "Show solution"}
                  </button>
                  {openAnswer === i ? (
                    <p className="mt-1 text-[12px] text-muted-foreground">{item.a}</p>
                  ) : null}
                </div>
              ))}
              <div className="rounded-xl border border-primary/25 bg-primary/[0.07] p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  Challenge
                </p>
                <p className="mt-1 text-[12.5px]">{def.challenge}</p>
              </div>
            </div>
          ) : null}

          {tab === "JEE" ? (
            <ul className="space-y-2">
              {(def.jeeNotes ?? def.theory).map((line) => (
                <li key={line} className="flex gap-2 text-[12.5px] leading-relaxed">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-sky-400" />
                  {line}
                </li>
              ))}
            </ul>
          ) : null}

          {tab === "Mistakes" ? (
            <ul className="space-y-2">
              {(def.mistakes ?? ["Re-read the formulae tab — sign and unit slips cost the most marks here."]).map(
                (line) => (
                  <li key={line} className="flex gap-2 text-[12.5px] leading-relaxed">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-orange-400" />
                    {line}
                  </li>
                ),
              )}
            </ul>
          ) : null}

          {tab === "Notes" ? (
            <textarea
              value={note}
              onChange={(e) => onNoteChange(e.currentTarget.value)}
              placeholder="Jot down what you noticed while playing with the sliders…"
              className="min-h-[120px] w-full resize-y rounded-xl bg-surface/60 p-3 text-[12.5px] outline-none ring-primary/40 focus:ring-2"
            />
          ) : null}
        </div>
      </div>
      </div>
    </div>

  );
}